import { Contract } from '@ethersproject/contracts';
import { GasSettings } from './types';
import { JsonRpcSigner, TransactionRequest } from '@ethersproject/providers';

export const GAS_LIMIT_BUFFER = 0.1;

// Chains where the wallet's fee estimate leaves near-zero headroom over the
// base fee (priority fee ~0, plus a low but block-to-block drifting base fee),
// which causes "max fee per gas less than block base fee" rejections when the
// base fee ticks up between estimation and inclusion. For these we attach an
// explicit legacy `gasPrice` with a buffer so drift can't invalidate the tx.
// The cost stays negligible on these chains.
export const BUFFERED_GAS_PRICE_CHAIN_IDS = [42161, 421614, 91342]; // Arbitrum One, Arbitrum Sepolia, GIWA Sepolia
export const GAS_PRICE_BASE_FEE_MULTIPLIER = 2;

export class GasService {
  public async settings(
    signer: JsonRpcSigner,
    options: TransactionRequest
  ): Promise<GasSettings> {
    const gasSettings: GasSettings = {};

    const gasLimit = await signer.estimateGas(options);
    gasSettings.gasLimit = this.formatGasLimit(gasLimit.toNumber());

    Object.assign(gasSettings, await this.bufferedGasPrice(signer.provider));

    return gasSettings;
  }

  public async settingsForContractCall(
    contractWithSigner: Contract,
    action: string,
    params: any[],
    options: Record<string, any>
  ): Promise<GasSettings> {
    const gasSettings: GasSettings = {};

    const gasLimit = await contractWithSigner.estimateGas[action](
      ...params,
      options
    );
    gasSettings.gasLimit = this.formatGasLimit(gasLimit.toNumber());

    Object.assign(
      gasSettings,
      await this.bufferedGasPrice(contractWithSigner.provider)
    );

    return gasSettings;
  }

  /**
   * For chains prone to base-fee-drift rejections (see
   * BUFFERED_GAS_PRICE_CHAIN_IDS), read the latest base fee and return a legacy
   * `gasPrice` of `baseFee * GAS_PRICE_BASE_FEE_MULTIPLIER`. A legacy gasPrice
   * sidesteps the EIP-1559 `maxFeePerGas < baseFee` check entirely and leaves
   * headroom for drift. On any error (or unaffected chain) returns `{}` so the
   * wallet's own estimation is used.
   */
  public async bufferedGasPrice(
    provider: JsonRpcSigner['provider']
  ): Promise<Pick<GasSettings, 'gasPrice'>> {
    try {
      const { chainId } = await provider.getNetwork();
      if (!BUFFERED_GAS_PRICE_CHAIN_IDS.includes(chainId)) return {};

      const block = await provider.getBlock('latest');
      const baseFee = block?.baseFeePerGas;
      if (!baseFee) return {};

      return {
        gasPrice: baseFee.mul(GAS_PRICE_BASE_FEE_MULTIPLIER).toNumber(),
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to compute buffered gas price', error);
      return {};
    }
  }

  private formatGasLimit(limit: number): number {
    return Math.floor(limit * (1 + GAS_LIMIT_BUFFER));
  }
}

export const gasService = new GasService();
